/**
 * Authentication Encryption Utility
 *
 * Provides secure login credential encryption using hybrid RSA+AES encryption.
 * Uses Web Crypto API for all cryptographic operations.
 *
 * Security features:
 * - RSA-OAEP-SHA256 (2048-bit) for key exchange
 * - AES-256-CBC for credential encryption
 * - Keys never stored in module state
 * - One-time use encryption keys per login
 */

/**
 * Generate RSA key pair for a login attempt
 * Returns fresh keys for each login for enhanced security
 *
 * @returns {Promise<CryptoKeyPair>} RSA key pair
 */
export async function initializeKeyPair(): Promise<globalThis.CryptoKeyPair> {
  try {
    const keyPair = await window.crypto.subtle.generateKey(
      {
        name: 'RSA-OAEP',
        modulusLength: 2048,
        publicExponent: new Uint8Array([1, 0, 1]), // 65537
        hash: 'SHA-256'
      },
      true, // extractable
      ['encrypt', 'decrypt']
    );

    return keyPair;
  } catch (error) {
    throw new Error('Failed to generate encryption keys');
  }
}

/**
 * Export public key in PEM format (SPKI)
 *
 * @param {CryptoKeyPair} keyPair - RSA key pair
 * @returns {Promise<string>} PEM-formatted public key
 */
export async function getPublicKeyPEM(keyPair: globalThis.CryptoKeyPair): Promise<string> {
  try {
    if (!keyPair.publicKey) {
      throw new Error('Public key is not available in key pair');
    }

    // Export public key in SPKI format
    const exported = await window.crypto.subtle.exportKey('spki', keyPair.publicKey);

    // Convert ArrayBuffer to Base64
    const exportedAsString = String.fromCharCode(...Array.from(new Uint8Array(exported)));
    const exportedAsBase64 = btoa(exportedAsString);

    // Format as PEM with line breaks every 64 characters
    const pemBody = exportedAsBase64.match(/.{1,64}/g)?.join('\n') ?? '';
    const pem = `-----BEGIN PUBLIC KEY-----\n${pemBody}\n-----END PUBLIC KEY-----`;

    return pem;
  } catch (error) {
    console.error('[AuthEncryption] Failed to export public key:', error);
    throw new Error('Failed to export public key');
  }
}

/**
 * Decrypt AES key using RSA private key
 *
 * @param {CryptoKeyPair} keyPair - RSA key pair
 * @param {string} encryptedKeyBase64 - Base64-encoded encrypted AES key
 * @returns {Promise<Uint8Array>} Decrypted AES key bytes
 */
export async function decryptAESKey(keyPair: globalThis.CryptoKeyPair, encryptedKeyBase64: string): Promise<Uint8Array> {
  try {
    if (!keyPair.privateKey) {
      throw new Error('Private key is not available in key pair');
    }

    // Convert Base64 to ArrayBuffer
    const encryptedKey = base64ToArrayBuffer(encryptedKeyBase64);

    // Decrypt using RSA private key
    const decryptedKey = await window.crypto.subtle.decrypt(
      {
        name: 'RSA-OAEP'
      },
      keyPair.privateKey,
      encryptedKey
    );

    return new Uint8Array(decryptedKey);
  } catch (error) {
    console.error('[AuthEncryption] Failed to decrypt AES key:', error);
    throw new Error('Failed to decrypt AES key');
  }
}

/**
 * Decrypt AES IV using RSA private key
 *
 * @param {CryptoKeyPair} keyPair - RSA key pair
 * @param {string} encryptedIVBase64 - Base64-encoded encrypted AES IV
 * @returns {Promise<Uint8Array>} Decrypted AES IV bytes
 */
export async function decryptAESIV(keyPair: globalThis.CryptoKeyPair, encryptedIVBase64: string): Promise<Uint8Array> {
  try {
    if (!keyPair.privateKey) {
      throw new Error('Private key is not available in key pair');
    }

    // Convert Base64 to ArrayBuffer
    const encryptedIV = base64ToArrayBuffer(encryptedIVBase64);

    // Decrypt using RSA private key
    const decryptedIV = await window.crypto.subtle.decrypt(
      {
        name: 'RSA-OAEP'
      },
      keyPair.privateKey,
      encryptedIV
    );

    return new Uint8Array(decryptedIV);
  } catch (error) {
    console.error('[AuthEncryption] Failed to decrypt AES IV:', error);
    throw new Error('Failed to decrypt AES IV');
  }
}

/**
 * Encrypt login credentials using AES-256-CBC
 *
 * @param {object} credentials - Credentials object (e.g. { Email, Password } or { OldPassword, NewPassword })
 * @param {Uint8Array} aesKey - AES key (32 bytes for AES-256)
 * @param {Uint8Array} aesIV - AES IV (16 bytes)
 * @returns {Promise<string>} Base64-encoded encrypted payload
 */
export async function encryptCredentials(credentials: Record<string, unknown>, aesKey: Uint8Array, aesIV: Uint8Array): Promise<string> {
  try {
    const credentialsJson = JSON.stringify(credentials);

    // Convert string to Uint8Array
    const encoder = new TextEncoder();
    const data = encoder.encode(credentialsJson);

    // Import AES key
    const cryptoKey = await window.crypto.subtle.importKey(
      'raw',
      aesKey,
      {
        name: 'AES-CBC',
        length: 256
      },
      false,
      ['encrypt']
    );

    // Encrypt data
    const encryptedData = await window.crypto.subtle.encrypt(
      {
        name: 'AES-CBC',
        iv: aesIV
      },
      cryptoKey,
      data
    );

    // Convert to Base64
    const encryptedArray = new Uint8Array(encryptedData);
    const encryptedBase64 = arrayBufferToBase64(encryptedArray);

    return encryptedBase64;
  } catch (error) {
    console.error('[AuthEncryption] Failed to encrypt credentials:', error);
    throw new Error('Failed to encrypt credentials');
  }
}

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Convert Base64 string to ArrayBuffer
 *
 * @param {string} base64 - Base64 string
 * @returns {ArrayBuffer}
 */
function base64ToArrayBuffer(base64: string): ArrayBuffer {
  const binaryString = atob(base64);
  const bytes = new Uint8Array(binaryString.length);
  for (let i = 0; i < binaryString.length; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }
  return bytes.buffer;
}

/**
 * Convert ArrayBuffer/Uint8Array to Base64 string
 *
 * @param {Uint8Array|ArrayBuffer} buffer
 * @returns {string}
 */
function arrayBufferToBase64(buffer: Uint8Array | ArrayBuffer): string {
  const bytes = buffer instanceof Uint8Array ? buffer : new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}
